import { motion } from 'framer-motion';
import { LegacyRef, useEffect, useState } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { FaFilter } from 'react-icons/fa6';
import useUserClickObserver from '../../../hooks/useUserClickObserver';

interface IAcademiaFilterSelect {
  options: Array<string>;
  onChangeHandler: (param: any) => void;
  defaultSelectedOption?: string;
  showSearchInput?: boolean;
  notFoundText?: string;
  isResetFilter?: boolean;
  setIsResetFilter?: React.Dispatch<React.SetStateAction<boolean>>;
  variant?: 'dark' | 'light';
}

const AcademiaFilterSelect = ({
  options,
  onChangeHandler,
  defaultSelectedOption,
  showSearchInput,
  notFoundText,
  isResetFilter,
  setIsResetFilter,
  variant = 'light',
}: IAcademiaFilterSelect) => {
  // integration of react hooks here
  const [showOptions, setShowOptions] = useState<boolean>(false);
  const [allOptions, setAllOptions] = useState<Array<string>>(options);
  const [selectValue, setSelectValue] = useState<string>(
    defaultSelectedOption || options[0]
  );
  const [searchText, setSearchText] = useState<string>('');

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowOptions);

  useEffect(() => {
    if (isResetFilter) {
      setSelectValue(options[0]);

      setIsResetFilter && setIsResetFilter(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResetFilter]);

  useEffect(() => {
    setAllOptions(
      options.filter((option) =>
        searchText
          ? option.toLowerCase().includes(searchText.toLowerCase())
          : option
      )
    );
  }, [options, searchText]);

  // rendering custom filter select field component here
  return (
    <div
      ref={selectRef as LegacyRef<HTMLDivElement>}
      className='relative w-full md:w-fit'
    >
      <div
        title={selectValue}
        onClick={() => {
          setSearchText('');
          setShowOptions((state) => !state);
        }}
        className={`font-semibold md:w-[250px] rounded-lg px-4 py-2 flex items-center justify-between cursor-pointer active:scale-95 duration-300 ${
          variant === 'light'
            ? 'bg-base-white text-legal-dark-blue'
            : 'bg-legal-dark-blue text-base-white'
        }`}
      >
        <div className='flex items-center gap-1 w-[90%]'>
          <FaFilter />
          <p className='md:w-[90%] truncate'>{selectValue}</p>
        </div>

        <FaChevronDown
          className={`${showOptions ? 'rotate-180' : 'rotate-0'} duration-300`}
        />
      </div>

      <motion.div
        initial={{ height: 0 }}
        animate={showOptions ? { height: 'fit-content' } : { height: 0 }}
        className='absolute left-0 top-full bg-gray-200 shadow-academia-sm w-full z-20 mt-2 rounded-md overflow-hidden text-base'
      >
        {showSearchInput && options.length > 5 ? (
          <div className='m-3'>
            <input
              tabIndex={-1}
              type='text'
              className='border-2 focus:ring-4 focus:ring-legal-dark-blue rounded-md px-3 py-2 w-full text-legal-dark-blue font-semibold duration-300 text-base border-legal-dark-blue active:scale-[0.98]'
              placeholder='Search...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>
        ) : null}

        <motion.ul
          initial={{ height: 0 }}
          animate={
            showOptions
              ? allOptions.length > 5
                ? { height: 220 }
                : { height: 'fit-content' }
              : { height: 0 }
          }
          className='overflow-y-auto academia-scrollbar w-full [&>li:not(:last-child)]:border-b [&>li:not(:last-child)]:border-legal-dark-blue [&>li:not(:last-child)]:border-dashed'
        >
          {allOptions.length ? (
            allOptions.map((option, index) => (
              <li
                key={index}
                onClick={() => {
                  setSelectValue(option);
                  setShowOptions(false);
                  onChangeHandler(option);
                }}
                className='px-2 py-1 text-center cursor-pointer font-semibold hover:bg-gray-50 hover:text-legal-dark-blue duration-300'
              >
                {option}
              </li>
            ))
          ) : (
            <p className='text-center mb-1 font-semibold text-base-red'>
              {notFoundText}
            </p>
          )}
        </motion.ul>
      </motion.div>
    </div>
  );
};

export default AcademiaFilterSelect;
